import { useContext, useMemo, useState } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { addDays, format, formatDistanceToNow, isSameDay } from 'date-fns';
import { sv, enUS } from 'date-fns/locale';
import {
  Lightning,
  CheckCircle,
  ArrowRight,
  CalendarBlank,
  Fire,
  Rocket,
  Storefront,
  CaretRight,
  CellSignalFull,
  HourglassMedium,
} from '@phosphor-icons/react';
import { CalendarDayScene, CalendarDayContext } from '@/contexts/CalendarDayContext';
import { CalendarContext } from '@/contexts/CalendarContext';
import { useStars } from '@/contexts/StarContext';
import { AppContext } from '@/contexts/AppContext';
import { getChildActivityStatus, getNextActivity, useShopHasItems } from '@/components/child';
import { usePendingStarValues } from '@/components/activity/usePendingStarValues';
import { dateLocales } from '@/i18n/dateLocales';
import { ActivityInstance } from '@api';
import CreateActivityFab from '@/components/layout/CreateActivityFab';
import VoiceActivityCreator from '@/components/voice/VoiceActivityCreator';
import { useNavigate } from 'react-router-dom';
import TeenUpgradeOnboarding from '@/pages/onboarding/TeenUpgradeOnboarding';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';
import { levelFromStars, levelTitleKey, starsToXp } from '@/utils/xp';

/**
 * TeenHome — tonåringens dagsida, byggd efter mockupen `dagens-vy-tonaring.html`.
 *
 * Ordningen på skärmen ÄR designen och ska inte kastas om:
 *   1. Header      Hej NAMN · titel · datum, och nivån som ett märke till höger.
 *   2. Samlat      EN ruta med tre rader: streak, butiken, och peppen med imorgon.
 *   3. Nästa upp   EN sak att gå vidare med — igångsättningsstödet.
 *   4. Dagen       rubrik + progress + listan, längst ner.
 *
 * Steg 2 var tidigare tre fristående kort (streak, butik, uppmuntran) utspridda över
 * sidan, varav ett UNDER listan. Tillsammans sköt de ner nästa aktivitet under vikningen
 * och gjorde dagsvyn till en anslagstavla. De bär fortfarande samma innehåll, men som
 * rader i en ruta — avläsning överst, handling under.
 *
 * FÄRGERNA KOMMER UR TEMAT, inte ur mockupen. Mockupens turkos är ETT av de teman
 * tonåringen kan välja (TEEN_THEMES), och att hårdkoda den hade tyst gjort temaväljaren
 * verkningslös på just den sida hen tittar mest på. Layout, ordning, innehåll och
 * tonfall följer mockupen; kulörerna läses ur getThemeTokens.
 */

const getRelativeTime = (date: Date, lang: string): string => {
  const locale = lang === 'sv' ? sv : enUS;
  return formatDistanceToNow(date, { addSuffix: true, locale });
};

/** CSS keyframes injected once */
const streakFireKeyframes = `
@keyframes teenStreakPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}
@keyframes teenCardEntrance {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
`;

const TeenHomeInner = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { instances, date } = useContext(CalendarDayContext);
  const { weekInstances } = useContext(CalendarContext);
  const { user } = useContext(AppContext);
  const { hasItems: shopHasItems, prices: shopPrices } = useShopHasItems();
  const { balance, streak } = useStars();
  const { isPending: isStarValuePending } = usePendingStarValues();
  const [voiceOpen, setVoiceOpen] = useState(false);
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);

  const locale = dateLocales[i18n.language] ?? dateLocales.en;
  const weekday = format(date, 'EEEE', { locale });
  const dateStr = format(date, 'd MMMM', { locale });

  const totalStars = balance.totalEarnedStars;
  // Samma trappa som framstegssidan (@/utils/xp). Headern och /progress får aldrig visa
  // olika nivå för samma stjärnor.
  const level = levelFromStars(totalStars);
  const levelTitle = t(levelTitleKey(level));
  const currentStreak = streak.currentStreak;

  const sorted = useMemo(
    () =>
      [...instances].sort(
        (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      ),
    [instances],
  );

  const nextUp = useMemo(() => getNextActivity(instances), [instances]);

  const doneCount = useMemo(
    () => sorted.filter((i) => getChildActivityStatus(i) === 'done').length,
    [sorted],
  );

  const totalCount = sorted.length;
  const progress = totalCount > 0 ? (doneCount / totalCount) * 100 : 0;

  // Butikens "nästa grej": billigaste varan tonåringen ÄNNU INTE har råd med. Finns
  // ingen sådan är allt inom räckhåll, och då säger stapeln ingenting vettig — då visas
  // bara saldot. Saldot är availableStars (intjänat minus spenderat), inte totalen:
  // det är det man faktiskt kan handla för.
  const spendableXp = starsToXp(balance.availableStars);
  const nextPriceStars = shopPrices.find((price) => price > balance.availableStars);
  const xpToNextItem = nextPriceStars === undefined ? 0 : starsToXp(nextPriceStars) - spendableXp;

  // En titt på imorgon. Veckovyns instanser finns redan i minnet, så det kostar ingen
  // extra förfrågan — och "imorgon väntar tre saker" är skillnaden mellan att stänga
  // appen och att veta vad som kommer.
  const tomorrowCount = useMemo(() => {
    const tomorrow = addDays(date, 1);
    return (weekInstances ?? []).filter((instance) =>
      isSameDay(new Date(instance.startDate), tomorrow),
    ).length;
  }, [weekInstances, date]);

  const activitiesLeft = totalCount - doneCount;
  const allDone = totalCount > 0 && activitiesLeft === 0;

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        // Platt, samma bakgrund som Vecka och Inställningar. En egen toning här
        // gjorde att bakgrunden skiftade när man bytte flik i mörkt läge.
        background: tk.bg,
        '@media (prefers-reduced-motion: no-preference)': {
          '& .teen-card': {
            animation: 'teenCardEntrance 0.3s ease-out both',
          },
        },
      }}
    >
      <Box component="style">{streakFireKeyframes}</Box>

      <Container maxWidth="sm" sx={{ pb: 12, px: { xs: 2, sm: 3 } }}>
        <Stack spacing={1.75} pt={2.5}>
          {/* ─── 1. Header: Hej NAMN · titel · datum · nivå ─── */}
          <Box
            className="teen-card"
            sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.6rem',
                  color: tk.text,
                  lineHeight: 1.15,
                }}
                noWrap
              >
                {t('teen.home.greetingHi', { name: user?.firstName ?? '' })}
              </Typography>

              {/* Titeln. Den är belöningen som inte kostar något — den syns bara om man
                  kommer hit, så den hör hemma här och inte bara på framstegssidan. */}
              <Typography
                sx={{ fontWeight: 600, fontSize: '0.875rem', color: tk.primary, mt: 0.5 }}
              >
                {levelTitle}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                <CalendarBlank size={15} weight="duotone" color={tk.textSoft} />
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    color: tk.textSoft,
                    fontWeight: 500,
                    textTransform: 'capitalize',
                  }}
                >
                  {weekday}, {dateStr}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: tk.card,
                borderRadius: '20px',
                px: 1.75,
                py: 1,
                flexShrink: 0,
                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
              }}
            >
              <CellSignalFull size={16} weight="fill" color={tk.primary} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: tk.primary }}>
                {t('teen.home.levelBadge', { level })}
              </Typography>
            </Box>
          </Box>

          {/* ─── 2. Samlad ruta: streak · butik · pepp ───
              ETT kort, inte tre flikar. Första försöket delade rutan med hårstreck, och
              då läste den fortfarande som tre staplade kort — bara med tunnare skarvar.
              Nu bär kortet innehållet direkt: streaken som rad, butiken som en knapp
              INUTI kortet, peppen som text. Ingen skarv går tvärs över ytan.

              Tonen är secondaryBg i båda lägena: ett steg djupare än de vita korten
              i ljust läge, och i mörkt läge mellan sidan (bg) och korten (card) —
              ramen skiljer den från sidan.

              Ingen hårdkodad kulör åt något håll — temaväljaren fortsätter gälla.

              Butiksknappen ligger i tk.card — ljusare än kortet den står i. Det är det
              som gör att den läser som ett element PÅ kortet i stället för en flik AV
              det, och den behåller sin egen träffyta: streaken och peppen är avläsning,
              inte navigering.

              Två detaljer ströks med flit. Dagens klarade/totalt stod HÄR och i listans
              rubrik längre ner — samma siffra två gånger är just det som gör en vy
              kladdig. Butikens framstegsstapel ströks också: den säger samma sak som
              raden "X XP kvar till nästa grej", fast långsammare. */}
          <Box
            className="teen-card"
            sx={{
              bgcolor: tk.secondaryBg,
              border: `1px solid ${tk.border}`,
              borderRadius: '18px',
              p: 2,
            }}
          >
            {/* Streaken till vänster, samlat XP till höger */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  width: 34,
                  height: 34,
                  borderRadius: '11px',
                  bgcolor: `${tk.primary}26`,
                  flexShrink: 0,
                  '@media (prefers-reduced-motion: no-preference)': {
                    '& .streak-fire': {
                      animation:
                        currentStreak > 0 ? 'teenStreakPulse 2s ease-in-out infinite' : 'none',
                    },
                  },
                }}
              >
                <Box className="streak-fire" sx={{ display: 'flex' }}>
                  {currentStreak > 0 ? (
                    <Fire size={19} weight="fill" color={tk.primary} />
                  ) : (
                    <Rocket size={19} weight="duotone" color={tk.textSoft} />
                  )}
                </Box>
              </Box>

              <Typography
                sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: '0.9rem', color: tk.text }}
                noWrap
              >
                {currentStreak > 0
                  ? t('teen.home.streakActive', { count: currentStreak })
                  : t('teen.home.streakNew')}
              </Typography>

              <Typography
                sx={{ fontSize: '0.8rem', color: tk.textSoft, fontWeight: 600, flexShrink: 0 }}
              >
                {t('teen.home.xpTotal', { xp: starsToXp(totalStars) })}
              </Typography>
            </Box>

            {/* Butiken — en knapp PÅ kortet, ljusare än ytan den ligger på */}
            {shopHasItems && (
              <Box
                component="button"
                type="button"
                onClick={() => navigate('/shop')}
                sx={{
                  mt: 1.75,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '12px',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  bgcolor: tk.card,
                  border: `1px solid ${tk.border}`,
                  transition: 'transform 0.12s ease',
                  '@media (hover: hover)': { '&:hover': { transform: 'translateX(2px)' } },
                  '&:active': { transform: 'scale(0.985)' },
                  '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                    '&:hover': { transform: 'none' },
                    '&:active': { transform: 'none' },
                  },
                }}
              >
                <Storefront size={20} weight="duotone" color={tk.primary} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: tk.text }} noWrap>
                    {t('teen.shop.entryTitle')}
                  </Typography>
                  {/* Bara när det FINNS något att spara till. Har man råd med allt är
                      raden ingen upplysning, och då står saldot till höger för sig. */}
                  {nextPriceStars !== undefined && (
                    <Typography
                      sx={{ fontSize: '0.75rem', color: tk.textSoft, fontWeight: 500 }}
                      noWrap
                    >
                      {t('teen.home.xpToNextItem', { xp: xpToNextItem })}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{ fontSize: '0.8rem', fontWeight: 700, color: tk.primary, flexShrink: 0 }}
                >
                  {t('teen.shop.price', { xp: spendableXp })}
                </Typography>
                <CaretRight size={16} weight="bold" color={tk.primary} />
              </Box>
            )}

            {/* Peppen — text på kortet, ingen egen platta */}
            {totalCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mt: 1.75 }}>
                <Typography sx={{ fontSize: '1rem', lineHeight: 1.3 }}>
                  {allDone ? '🎉' : '💪'}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Entalsformen väljs HÄR och inte med i18next-plural: resten av
                      filerna använder inga plural-suffix, och en saknad _one-form hade
                      visat själva nyckeln i stället för en mening. */}
                  <Typography
                    sx={{ fontSize: '0.8rem', color: tk.text, fontWeight: 600, lineHeight: 1.35 }}
                  >
                    {allDone
                      ? t('teen.home.encourageAllDone')
                      : activitiesLeft === 1
                      ? t('teen.home.encourageBodyOne')
                      : t('teen.home.encourageBody', { count: activitiesLeft })}
                  </Typography>
                  {tomorrowCount > 0 && (
                    <Typography
                      sx={{ fontSize: '0.75rem', color: tk.textSoft, fontWeight: 500, mt: 0.25 }}
                    >
                      {t('teen.home.tomorrowLabel')}{' '}
                      {tomorrowCount === 1
                        ? t('teen.home.tomorrowCountOne')
                        : t('teen.home.tomorrowCount', { count: tomorrowCount })}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* ─── 3. Nästa upp ───
              Neutral yta, tydlig plats. Kortet låg förut i en primärfärgad platta med
              primärfärgad ram och en helfärgad ikonruta — tre lager kulör på rad, direkt
              under en samlad ruta som också är tonad. Två färgade block ovanpå varandra
              gör att ingetdera sticker ut.
              Nu bär det tk.card, alltså den ljusaste ytan på sidan, mot den tonade rutan
              ovanför. Det är kontrasten som gör det tydligt — inte mättnaden. Kvar i
              primärfärg står bara mikroetiketten NÄSTA UPP och klockslaget: det är de
              som säger vad kortet ÄR. */}
          {nextUp && (
            <Box
              className="teen-card"
              component="button"
              type="button"
              onClick={() => navigate(`/activity/${nextUp.instance.activityInstanceId}`)}
              sx={{
                width: '100%',
                textAlign: 'left',
                fontFamily: 'inherit',
                cursor: 'pointer',
                bgcolor: tk.card,
                border: `1px solid ${tk.border}`,
                boxShadow: '0 2px 10px rgba(20,80,85,0.07)',
                borderRadius: '16px',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.75,
                transition: 'transform 0.15s ease',
                '@media (hover: hover)': { '&:hover': { transform: 'scale(1.01)' } },
                '&:active': { transform: 'scale(0.985)' },
                '@media (prefers-reduced-motion: reduce)': {
                  transition: 'none',
                  '&:hover': { transform: 'none' },
                  '&:active': { transform: 'none' },
                },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  bgcolor: `${tk.primary}26`,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Lightning size={20} weight="fill" color={tk.primary} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    color: tk.primary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    mb: 0.25,
                  }}
                >
                  {t('teen.home.nextUp')}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: tk.text }} noWrap>
                  {nextUp.instance.activityName}
                </Typography>
                {/* Ungefärlig tid FÖRST, exakt klockslag framhävt efter. "Om ungefär tre
                    timmar" är det som svarar på frågan man ställer sig; klockslaget är
                    det man behöver när svaret är "snart". */}
                <Typography sx={{ fontSize: '0.8rem', color: tk.textSoft, fontWeight: 500 }}>
                  {getRelativeTime(new Date(nextUp.instance.startDate), i18n.language)}
                  {' · '}
                  <Box component="span" sx={{ color: tk.primary, fontWeight: 600 }}>
                    {t('teen.home.atTime', {
                      time: format(new Date(nextUp.instance.startDate), 'HH:mm'),
                    })}
                  </Box>
                  {nextUp.instance.duration > 0 &&
                    ` · ${nextUp.instance.duration} ${t('teen.home.min')}`}
                </Typography>
              </Box>
              <ArrowRight size={20} weight="bold" color={tk.textSoft} />
            </Box>
          )}

          {/* ─── 4. Dagens aktiviteter, längst ner ─── */}
          {totalCount > 0 && (
            <Box className="teen-card" sx={{ pt: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  mb: 1,
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: tk.text }}>
                  {t('teen.home.todayTitle')}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: tk.textSoft, fontWeight: 600 }}>
                  {t('teen.home.doneCount', { done: doneCount, total: totalCount })}
                </Typography>
              </Box>

              <Box
                sx={{
                  height: 8,
                  borderRadius: '6px',
                  bgcolor: tk.border,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${progress}%`,
                    borderRadius: '6px',
                    background: `linear-gradient(90deg, ${tk.accent}, ${tk.primary})`,
                    transition: 'width 0.5s ease-out',
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                />
              </Box>
            </Box>
          )}

          {sorted.length === 0 ? (
            <Box
              className="teen-card"
              sx={{
                bgcolor: tk.card,
                border: `1px dashed ${tk.border}`,
                borderRadius: '16px',
                p: 4,
                textAlign: 'center',
              }}
            >
              <Rocket size={36} weight="duotone" color={tk.textSoft} />
              <Typography sx={{ fontWeight: 600, color: tk.textSoft, mt: 1.5, fontSize: '0.9rem' }}>
                {t('teen.home.empty')}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: tk.textSoft, mt: 0.5 }}>
                {t('teen.home.emptyHint')}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {sorted.map((inst: ActivityInstance, index: number) => {
                const status = getChildActivityStatus(inst);
                const isDone = status === 'done';
                const isMissed = status === 'missed';
                // Väntar aktivitetens XP-värde på förälderns besked ger den ingenting
                // när den klaras av. Det ska stå på kortet, inte bara ha stått en gång
                // när aktiviteten skapades.
                const isValuePending = isStarValuePending(inst);

                return (
                  <Box
                    key={inst.activityInstanceId}
                    className="teen-card"
                    component="button"
                    type="button"
                    onClick={() => navigate(`/activity/${inst.activityInstanceId}`)}
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: '14px 16px',
                      borderRadius: '14px',
                      border: 'none',
                      bgcolor: isDone ? `${tk.primary}1F` : tk.card,
                      boxShadow: isDone ? 'none' : '0 2px 8px rgba(20,80,85,0.06)',
                      transition: 'transform 0.12s ease',
                      '@media (hover: hover)': {
                        '&:hover': { transform: 'translateX(2px)' },
                      },
                      '&:active': { transform: 'scale(0.98)' },
                      '@media (prefers-reduced-motion: reduce)': {
                        transition: 'none',
                        '&:hover': { transform: 'none' },
                        '&:active': { transform: 'none' },
                      },
                      animationDelay: `${index * 50}ms`,
                    }}
                  >
                    <Box sx={{ width: 56, flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: tk.text }}>
                        {format(new Date(inst.startDate), 'HH:mm')}
                      </Typography>
                      {inst.duration > 0 && (
                        <Typography
                          sx={{ fontSize: '0.75rem', fontWeight: 500, color: tk.textSoft }}
                        >
                          {inst.duration} {t('teen.home.min')}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          color: isDone ? tk.textSoft : tk.text,
                          textDecoration: isDone ? 'line-through' : 'none',
                        }}
                        noWrap
                      >
                        {inst.activityName}
                      </Typography>
                      {isMissed && (
                        <Typography
                          sx={{ fontSize: '0.75rem', fontWeight: 500, color: tk.textSoft }}
                        >
                          {t('teen.home.skipped')}
                        </Typography>
                      )}
                      {isValuePending && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <HourglassMedium size={12} weight="bold" color={tk.textSoft} />
                          <Typography
                            sx={{ fontSize: '0.75rem', fontWeight: 500, color: tk.textSoft }}
                          >
                            {t('starValue.pendingBadge')}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {isDone ? (
                      <CheckCircle size={26} weight="fill" color={tk.primary} />
                    ) : (
                      <ArrowRight size={18} weight="bold" color={tk.textSoft} />
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Container>

      <CreateActivityFab onVoiceClick={() => setVoiceOpen(true)} />
      <VoiceActivityCreator
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        onSaved={() => setVoiceOpen(false)}
      />
    </Box>
  );
};

const TEEN_ONBOARDING_KEY = 'teen-onboarding-seen';

const TeenHome = () => {
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem(TEEN_ONBOARDING_KEY),
  );

  if (showOnboarding) {
    return (
      <CalendarDayScene date={new Date()}>
        <TeenUpgradeOnboarding
          onComplete={() => {
            localStorage.setItem(TEEN_ONBOARDING_KEY, 'true');
            setShowOnboarding(false);
          }}
        />
      </CalendarDayScene>
    );
  }

  return (
    <CalendarDayScene date={new Date()}>
      <TeenHomeInner />
    </CalendarDayScene>
  );
};

export default TeenHome;
